import { EnvironmentConfig, SystemRoles } from '@/backend/extraTypes';
import { accountsLinks } from '@/backend/routes';
import { useUserAppRoutes } from '@/ui/UserAppRoutes';
import { pendo } from '@/ui/constants';
import { ConfigLoader } from '@/ui/providers/ConfigLoader';
import { visuallyHidden } from '@/ui/utils/visuallyHidden';
import { useInboxBadge } from '@juvare/alert-ui/ui/features/inbox';
import { AlertConfigProvider } from '@juvare/alert-ui/ui/providers';
import { DialogHelper } from '@juvare/common-web-ui/DialogHelper';
import { GlobalApp } from '@juvare/common-web-ui/GlobalApp';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { SnackbarHelper } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateCustom } from '@juvare/common-web-ui/utils';
import { allowAccessToAccounts } from '@juvare/contact-management/backend/features/permissions/shared';
import { AccountAvatar } from '@juvare/contact-management/ui/features/accounts';
import { EditablePageData } from '@juvare/platform-sdk-react/backend/features/editable-pages/shared';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import { getUserState, platformSlice } from '@juvare/platform-sdk-react/ui/stores';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction, Outlet } from 'react-router';
import { AppContext } from './Context';
import { LoginCompletionApp } from './LoginCompletionApp';
import { baseName } from './constants';
import {
    useIdleUserMonitor,
    useRedirectAfterLogin,
    useSettingsMenu,
    useSupportCenterMenu,
    useSwitchUserInfo,
    useWindowTitle,
} from './hooks';
import { ContactManagementConfigProviderUser, CredentialingProvider, FormConfigContextProvider } from './providers';
import { getGlobalHeaderEnvironment } from './utils';

interface UserAppProps {
    config: EnvironmentConfig;
    pages: EditablePageData[];
}

export const UserApp: React.FC<UserAppProps> = ({ config, pages }) => {
    const { initState } = React.useContext(AppContext);
    const userState = useSelector(getUserState);
    const settings = useSelector(getSettings);
    const userClient = useUserClient();
    const { t } = useTranslation();
    const navigationContext = React.useContext(NavigationContext);
    const dispatch = useDispatch();

    useWindowTitle();
    useIdleUserMonitor();
    const info = useSwitchUserInfo();
    const { supportCenterRoutes, supportCenterHeaderItem } = useSupportCenterMenu(pages);
    const { settingsRoutes, settingsHeaderItems } = useSettingsMenu();
    const { createUserAppRoutes } = useUserAppRoutes();

    React.useEffect(() => {
        if (userState?.id) {
            pendo.initialize({
                visitor: {
                    id: userState.id,
                    role: userState.systemRole,
                },
                account: {
                    id: config.clientId,
                    environment: config.environment,
                    product: 'RMS',
                },
            });
        }
    }, [userState?.id, userState?.systemRole, config.clientId, config.environment]);

    if (userState && userState.loginValidationResult.incompleteSections.length > 0) {
        return <LoginCompletionApp />;
    }

    const onSearch = allowAccessToAccounts(userState)
        ? (navigate: NavigateFunction, partialSearch: string, closeSearch: () => void) => {
              const urlParams = new URLSearchParams();
              urlParams.set('filter', JSON.stringify({ partialSearch }));
              navigate({ pathname: accountsLinks.root, search: urlParams.toString() });
              closeSearch();
          }
        : undefined;

    const logOutOnClick = () => {
        return navigationContext.navigate(() => {
            userClient.logout().finally(() => {
                initState();
                dispatch(platformSlice.actions.clearUser());
            });
        });
    };

    const headerPrimaryItem = settings.enablePendoIntegration
        ? {
              icon: <NewReleasesIcon />,
              label: t('common.resourceCenter'),
              'data-test': 'whats-new',
          }
        : undefined;

    if (userState) {
        return (
            <GlobalApp
                appTitle={<b>{settings.appTitle}</b>}
                environment={getGlobalHeaderEnvironment(config)}
                basename={baseName}
                routes={[...createUserAppRoutes(), ...supportCenterRoutes, ...settingsRoutes]}
                oktaUrl={config.oktaUrl}
                appImage={settings.showClientLogo ? <img src={settings.clientLogo} height='60' alt='' /> : undefined}
                searchLabel={t('appHeader.globalSearchLabel')}
                onSearch={onSearch}
                headerItems={[supportCenterHeaderItem, ...settingsHeaderItems]}
                headerPrimaryItem={headerPrimaryItem}
                profileAvatarSmall={<AccountAvatar size='small' />}
                profileAvatarLarge={<AccountAvatar size='large' />}
                profileName={userState.displayName}
                profileBadge={
                    userState.incompleteAccountSections > 0 ? (
                        <Box>
                            {userState.incompleteAccountSections} <Box sx={visuallyHidden}>{t('appHeader.profileBadgeAttention')}</Box>
                        </Box>
                    ) : undefined
                }
                profileRole={t(`roles.${userState.systemRole as SystemRoles}`)}
                profileNavigateTo={accountsLinks.root + '/' + userState.id}
                logOutOnClick={logOutOnClick}
                {...info}
                footerCopyright={t('footer.message', { copyrightYear: formatDateCustom(new Date(__BUILD_TIMESTAMP__), 'yyyy') })}
                footerLinks={[
                    { label: t('footer.privacyPolicy'), href: 'https://www.juvare.com/privacy-policy/' },
                    { label: t('footer.termsAndConditions'), href: 'https://www.juvare.com/terms-conditions/' },
                ]}
                rootElement={(children) => (
                    <AlertConfigProvider
                        features={settings.featureFlags.notifications}
                        // TODO types must be fixed for Provider
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        notificationOptions={settings.notificationOptions as any}
                        notificationRichTextMessageBody={settings.notificationRichTextMessageBody}
                        availableChannels={settings.availableChannels}
                    >
                        <ContactManagementConfigProviderUser>
                            <CredentialingProvider>{children}</CredentialingProvider>
                        </ContactManagementConfigProviderUser>
                    </AlertConfigProvider>
                )}
            >
                <UserAppOutlet />
            </GlobalApp>
        );
    } else {
        throw new Error('Unauthorized');
    }
};

const UserAppOutlet: React.FC = () => {
    useRedirectAfterLogin();
    useInboxBadge();
    return (
        <FormConfigContextProvider>
            <ConfigLoader>
                <DialogHelper />
                <SnackbarHelper />
                <Outlet />
            </ConfigLoader>
        </FormConfigContextProvider>
    );
};
